import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Portal } from "@radix-ui/react-tooltip";
import { cn } from "@/lib/utils";

interface SqTooltipProviderProps {
  children: React.ReactNode;
  delayDuration?: number;
}

const SqTooltipProvider: React.FC<SqTooltipProviderProps> = ({
  children,
  delayDuration = 300,
}) => (
  <TooltipPrimitive.Provider delayDuration={delayDuration}>
    {children}
  </TooltipPrimitive.Provider>
);

const SqTooltip = TooltipPrimitive.Root;

interface SqTooltipTriggerProps extends React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Trigger
> {
  tooltipContentDisabled?: boolean;
}

const SqTooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  SqTooltipTriggerProps
>(({ className, tooltipContentDisabled, ...props }, ref) => (
  <TooltipPrimitive.Trigger
    ref={ref}
    className={cn("cursor-default", className)}
    {...(tooltipContentDisabled ? { style: { pointerEvents: "none" } } : {})}
    {...props}
  />
));
SqTooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

interface SqTooltipContentProps extends React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
> {
  children: React.ReactNode;
  portal?: boolean;
}

const SqTooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  SqTooltipContentProps
>(({ className, sideOffset = 4, children, portal = false, ...props }, ref) => {
  const content = (
    <TooltipPrimitive.Content
      className={cn(
        "z-[100] text-white text-[12px] px-2 py-1 min-w-0 max-w-[240px] squircle squircle-fill-[rgba(0,0,0,0.95)]",
        className,
      )}
      ref={ref}
      sideOffset={sideOffset}
      {...props}
    >
      {children}
    </TooltipPrimitive.Content>
  );

  return portal ? <Portal>{content}</Portal> : content;
});
SqTooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { SqTooltip, SqTooltipTrigger, SqTooltipContent, SqTooltipProvider };
